/**
 * The account's task categories, in an order someone can find a name in (FE-98).
 *
 * Chandler Kerrigan, 2026-09-22, for Exer: *"can we alphabetize this list pretty please :) requested by exer."* The
 * pickers listed the catalogue exactly as the API returned it, which is creation order, so a list holding both site
 * names and workflow categories read as unsorted — and an account with dozens of them has no way to scan it.
 *
 * Sorting lives here, not in each picker, because two pickers render this catalogue and a third would otherwise be
 * unsorted again.
 */

/** Anything the pickers render: what they sort by is the label. */
export interface ICategoryOptionLike {
    label?: string;
}

/**
 * The same options, ordered by label.
 *
 * Case-insensitive and locale-aware, with numbers compared as numbers: `PROVIDER_FOLLOWUP` belongs under P rather than
 * ahead of every lower-case name, and "Site 10" comes after "Site 9" rather than before it. An option with no label
 * sorts last instead of being dropped — it is still a category the task can carry, and hiding one would be a worse
 * answer than showing it at the end.
 *
 * A copy, not a sort in place: these arrays come from component state, and sorting one in place mutates what React is
 * already holding. Equal labels keep the order the server sent, because the sort is stable.
 */
export function sortCategoryOptions<T extends ICategoryOptionLike>(options: T[]): T[] {
    return [...options].sort((left, right) => {
        const a = (left.label || '').trim();
        const b = (right.label || '').trim();
        if (!a || !b) {
            return a === b ? 0 : a ? -1 : 1;
        }
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
    });
}
